// src/app/services/auth.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TokenStorageService } from './token-storage.service';
import { AuthStateService } from './auth-state.service';

const AUTH_API = 'http://localhost:8081/api/auth/';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' }),
  withCredentials: true,
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  constructor(private http: HttpClient, private tokenStorage: TokenStorageService, private authState: AuthStateService) {}

  login(email: string, password: string): Observable<any> {
    return this.http.post(
      AUTH_API + 'signin',
      {
        username: email, // Utilisez l'email comme nom d'utilisateur
        password,
      },
      httpOptions
    ).pipe(
      tap((res: any) => {
        // Persist JWT and basic user info for subsequent API calls
        if (res?.token) {
          this.tokenStorage.saveToken(res.token);
          // Save minimal user info (can be enriched after /users/profile)
          this.tokenStorage.saveUser({
            id: res.id,
            firstName: res.firstName,
            lastName: res.lastName,
            email: res.email,
            roles: res.roles || []
          });
          this.authState.notifyLogin();
        }
      })
    );
  }

  loginAdmin(email: string, password: string, adminCode: string): Observable<any> {
    return this.http.post(
      AUTH_API + 'signin-admin',
      {
        username: email,
        password,
        adminCode
      },
      httpOptions
    ).pipe(
      tap((res: any) => {
        if (res?.token) {
          this.tokenStorage.saveToken(res.token);
          this.tokenStorage.saveUser({
            id: res.id,
            firstName: res.firstName,
            lastName: res.lastName,
            email: res.email,
            roles: res.roles || []
          });
          this.authState.notifyLogin();
        }
      })
    );
  }

  registerAdmin(email: string, password: string, adminCode: string): Observable<any> {
    // Backend expects AdminSignupRequest with fields: email, password, adminCode
    const payload = {
      email,
      password,
      adminCode
    };
    return this.http.post(
      AUTH_API + 'signup-admin',
      payload,
      httpOptions
    );
  }

  logout(): void {
    try {
      // Defensive clear of legacy storage
      try { localStorage.removeItem('auth-token'); } catch {}
      try { localStorage.removeItem('auth-user'); } catch {}
      // Clear in-memory token storage
      this.tokenStorage.signOut();
    } finally {
      this.authState.notifyLogout();
      // Use router-friendly redirect (full reload not required unless you need hard reset)
      window.location.assign('/');
    }
  }

  register(
    firstName: string,
    lastName: string,
    email: string,
    username: string,
    password: string,
    options?: {
      phoneCountryCode?: string;
      phone?: string;
      company?: string;
      jobTitle?: string;
      avatarUrl?: string;
    }
  ): Observable<any> {
    const payload: any = {
      firstName,
      lastName,
      username: email, // Utilisez l'email comme nom d'utilisateur
      email,
      password,
    };
    if (options) {
      if (options.phoneCountryCode) payload.phoneCountryCode = options.phoneCountryCode;
      if (options.phone) payload.phone = options.phone;
      if (options.company) payload.company = options.company;
      if (options.jobTitle) payload.jobTitle = options.jobTitle;
      if (options.avatarUrl) payload.avatarUrl = options.avatarUrl;
    }
    return this.http.post(
      AUTH_API + 'signup',
      payload,
      httpOptions
    );
  }
}
